import type { ValueFormat } from '@forgd/formatter'
import type { ChartOptions, LegendOptions } from './composable'
import type { DataRecord } from './model'

// @see https://github.com/forged-com/forgd/blob/dev/apps/ui/core/services/unovis/README.md#helper-functions

// ---------------------------------------------------------------------------------------------------------------------
// model and interface
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Helper function to generate English labels
 *
 * @param record  A data record
 * @param labels  An optional hash of labels to use instead of reformatted key names
 */
export function makeLabels<R extends DataRecord>(record: R, labels?: Partial<Record<keyof R, string>>) {
  return Object
    .keys(record || {})
    .reduce((output, key: keyof R) => {
      output[key] = labels && key in labels
        ? labels[key] // use labels if provided
        : String(key).includes(' ')
          ? key // use server-provided strings with spaces as-is
          : formatKey(String(key)) // otherwise, split and format string
      return output
    }, {} as Record<keyof R, any>)
}

// ---------------------------------------------------------------------------------------------------------------------
// labels and legend
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Helper function to grab x and y labels
 *
 * @param labels  A hash of labels including both x and y labels
 * @param xAxis   The chosen xAxis key, e.g. "month"
 */
export function makeAxisLabels<T extends DataRecord>(labels: Record<keyof T, string>, xAxis: keyof T) {
  const { y } = splitRecord(labels, xAxis)
  return {
    x: labels[xAxis],
    y: y as Record<keyof typeof labels, string>, // casting as it's easier than juggling Omit types
  }
}

/**
 * Helper function to generate legend options
 *
 * @param labels  A hash of labels including both x and y labels
 * @param xAxis   The chosen xAxis key, e.g. "month"
 * @param colors  An array of colors
 * @param format  A ValueFormat constant to format values
 */
export function makeLegendOptions<T extends DataRecord>(labels: Record<keyof T, string>, xAxis: keyof T, colors: string[], format?: ValueFormat): LegendOptions<T> {
  const { y } = splitRecord(labels, xAxis)
  return Object
    .entries(y)
    .reduce((output, [key], index) => {
      const label = labels[key as keyof T] ?? String(key)
      output[key as keyof T] = {
        axis: 'y',
        label,
        color: getColor(index, colors)!,
        format,
        value: label,
      }
      return output
    }, {} as LegendOptions<T>)
}

/**
 * Helper function to create an initial reactive value from chart data array
 *
 * @param data
 * @param index
 */
export function makeHovered<R extends DataRecord>(data: R[], index = -1) {
  return reactive<R>(data ? clone(data.at(index)) : {})
}

// ---------------------------------------------------------------------------------------------------------------------
// unovis accessors and properties
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Helper function to generate Chart component x and y accessors from a ChartModel object
 *
 * @param labels
 * @param xAxis
 * @param keys    An optional list of keys to extract accessors for (defaults to all)
 * @usage
 *
 *  <VisLine v-bind="makePointAccessors(data, 'time')" />
 *  <VisLine v-bind="makePointDataAccessors(data, 'time', 'foo', 'bar)" />
 */
export function makePointAccessors<T extends Record<string, any>>(labels: T, xAxis: string, ...keys: Array<keyof T>) {
  const { y } = splitRecord(labels, xAxis)
  keys = keys.length === 0
    ? Object.keys(y)
    : keys
  return {
    x: (record: T) => record[xAxis],
    y: keys
      .reverse() // reversed so that stack order matches legend order
      .map((key: keyof T) => (record: T) => {
        return record[key as any]
      }),
  }
}

/**
 * Helper function to generate Chart component color accessors
 *
 * @param colors
 */
export function makeColorAccessors(colors: string[]) {
  return (_values: DataRecord[], index: number) => {
    return getColor(index, [...colors].reverse())
  }
}

/**
 * Helper function to calculate bar width given chart width, gap and margin
 *
 * @param data
 * @param options
 * @param axisWidth
 */
export function getBarWidth<T>(data: T[], options: ChartOptions, axisWidth = 0): number | undefined {
  const { width, gap, margin = 0 } = options
  if (width && gap) {
    const xMargin = typeof margin === 'number'
      ? margin * 2
      : (margin?.left ?? 0) + (margin?.right ?? 0)
    const numValues = data.length
    const widthChart = width - axisWidth - xMargin
    const widthGaps = (numValues - 1) * gap
    return Math.floor((widthChart - widthGaps) / numValues)
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// ranges
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Get the min and max values from a range of data
 *
 * @param records   An array of data records
 * @param xAxis
 */
export function getDataBounds<R extends DataRecord>(records: R[], xAxis: keyof R) {
  let min: number | undefined
  let max: number | undefined
  for (const record of records) {
    const values = Object.values(splitRecord(record, xAxis).y) as number[]
    const vMin = Math.min(...values)
    const vMax = Math.max(...values)
    if (min === undefined || vMin < min) {
      min = vMin
    }
    if (max === undefined || vMax > max) {
      max = vMax
    }
  }
  return { min: min || 0, max: max || 0 }
}

/**
 * Get the chart domain (y min and y max), optionally rounded to the nearest power of 10
 *
 * @param records   An array of data records
 * @param xAxis
 * @param stacked   Whether the chart is of a stacked type (so need to total values)
 */
export function getDomain<R extends DataRecord>(records: R[], xAxis: keyof R, stacked = false, rounded = false) {
  // values
  let min: number | undefined
  let max: number | undefined

  // stacked
  if (stacked) {
    min = 0
    for (const record of records) {
      const { y } = splitRecord(record, xAxis)
      const total = sum(Object.values(y))
      if (max === undefined || total > max) {
        max = total
      }
    }
  }
  else {
    const values = getDataBounds(records, xAxis)
    min = values.min
    max = values.max
  }

  // output
  min = min || max || 0
  max = max || min || 0

  // return
  if (rounded) {
    return [
      roundToPower(min, -1),
      roundToPower(max, 1),
    ]
  }
  return [min, max]
}

// ---------------------------------------------------------------------------------------------------------------------
// data
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Return normalised values (0-100 percent) for the entire array of DataRecords
 *
 * @param records
 * @param xAxis
 */
export function getNormalisedValues<R extends DataRecord>(records: R[], xAxis: keyof R) {
  return records.map((record: R) => {
    const total = sumValues(record, xAxis)
    return Object.keys(record).reduce((output, key) => {
      ;(output as any)[key] = key === xAxis
        ? record[xAxis]
        : record[key]! / total * 100
      return output
    }, {} as DataRecord)
  })
}

/**
 * Get only the y-axis values (omitting the x data) from all records
 *
 * @param records   The array of records
 * @param xAxis     The x-axis key
 */
export function getYAxisValues<R extends DataRecord, K extends keyof R>(records: R[], xAxis: K): Omit<R, K>[] {
  return records.map((record: R) => {
    const { y } = splitRecord(record, xAxis)
    return y
  })
}

/**
 * Parse a record into x and y components
 *
 * @param record    A single data record
 * @param xAxis     The x-axis key
 */
export function splitRecord<K extends keyof R, R extends Record<string, any>>(record: R, xAxis: K): { x: R[K], y: Omit<R, K> } {
  const { [xAxis]: x, ...y } = record
  return { x, y }
}

/**
 * Reverse the order of keys in a DataRecord
 *
 * Used to present the chart tooltip legend in the same order as the stacked bars
 *
 * @param data
 */
export function flipRecord<T extends Record<string, any>>(data: T): T {
  return Object
    .keys(data)
    .reverse()
    .reduce((output, key) => {
      (output as any)[key] = data[key]
      return output
    }, {} as T)
}

/**
 * Total the values in a single data record
 *
 * Used to calculate percentages for normalised data
 *
 * @param record
 * @param xAxis
 */
export function sumValues<R extends DataRecord>(record: R, xAxis: keyof R) {
  const { y } = splitRecord(record, xAxis)
  const values = Object.values(y as DataRecord)
  return sum(values)
}

// ---------------------------------------------------------------------------------------------------------------------
// utils
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Get a color from an array of colors, looping round if out of bounds
 *
 * @param index
 * @param colors
 */
export function getColor(index: number, colors?: string[]) {
  return colors
    ? colors[index % colors.length]
    : undefined
}

/**
 * Round a number up or down to the nearest power ot 10
 *
 * @param value
 * @param round
 */
function roundToPower(value: number, round: -1 | 1): number {
  const sign: number = Math.sign(value)
  const absNum: number = Math.abs(value)
  const magnitude: number = Math.floor(Math.log10(absNum))
  const scale: number = 10 ** magnitude

  const fn = round > 0 ? Math.ceil : Math.floor
  return sign * fn(absNum / scale) * scale
}

/**
 * Sum an array of numbers
 */
function sum(values: number[]) {
  return values.reduce((a, b) => a + b)
}

/**
 * Simple clone function
 */
export function clone(value: any) {
  return value === undefined ? value : JSON.parse(JSON.stringify(value))
}

/**
 * Format a key to more English-looking text
 */
export function formatKey(key: string) {
  return key
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, c => c.toUpperCase())
}
